import React, { useEffect, useRef } from 'react';
import { Collapse, TableRow, TableCell, Tooltip, Typography } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import SortableTable from '../../shared/Tables/SortableTable';
import SeverityBadge from './SeverityBadge';
import StatusPill from './StatusPill';
import { ruleLabel } from './ruleLabels';
import { formatDate } from '../../../../helpers/utils';

const useStyles = makeStyles((theme) => ({
  contentSection: {
    padding: '16px 22px',
    background: '#fff',
    marginBottom: '15px',
    borderRadius: '3px',
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionHeaderTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#000',
  },
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  tabActive: {
    color: '#1E69E5',
  },
  detailsValue: {
    fontFamily: 'monospace',
    fontSize: '12px',
    display: 'inline-block',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    cursor: 'default',
  },
}));

const HEAD_CELLS = [
  { id: 'type', label: 'Type' },
  { id: 'field', label: 'Field' },
  { id: 'details', label: 'Details' },
  { id: 'severity', label: 'Severity' },
  { id: 'occurrence_count', label: 'Occurrences' },
  { id: 'first_seen', label: 'First Seen' },
  { id: 'last_seen', label: 'Last Seen' },
  { id: 'status', label: 'Status' },
];

/**
 * Read-only accordion listing historical Data Integrity warnings for a single
 * contact/investor (GEN-2367), grouped by (rule, field) - one row per distinct
 * warning with occurrence count and Active/Resolved status. Shared between the
 * contact and investor edit modals since the shape is entity-agnostic.
 */
const DataIntegrityWarningsSection = ({ warnings, fetching, tab, handleTabChange, scroll, tabValue = 'data_integrity_warnings' }) => {
  const classes = useStyles();
  const sectionRef = useRef(null);

  useEffect(() => {
    if (scroll && !fetching) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [fetching, scroll]);

  return (
    <div className={classes.contentSection} ref={sectionRef}>
      <div className={classes.contentSectionHeader} onClick={(event) => handleTabChange(event, tabValue)}>
        <Typography variant='h5' className={`${classes.contentSectionHeaderTitle} ${tab === tabValue ? classes.tabActive : ''}`}>
          Data Integrity Warnings
        </Typography>
        {tab === tabValue ? <ExpandLess /> : <ExpandMore />}
      </div>
      <Collapse in={tab === tabValue}>
        <div className={classes.formGroup}>
          {
            fetching ? <Skeleton animation='pulse' variant='rect' height='100px' width='100%' component='div' /> : (
              warnings.length === 0 ? (
                <Typography variant='body2' color='textSecondary'>No data integrity warnings for this record.</Typography>
              ) : (
                <SortableTable
                  disableSort
                  headCells={HEAD_CELLS}
                  rows={warnings}
                  RowRenderer={(row, _isSelected, index) => (
                    <TableRow hover tabIndex={-1} key={`${row.rule.id}-${row.field}-${index}`}>
                      <TableCell>{ruleLabel(row.rule.code)}</TableCell>
                      <TableCell>{row.field}</TableCell>
                      <TableCell>
                        <Tooltip title={row.message || 'No additional details'}>
                          <span className={classes.detailsValue}>{row.value || row.message || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell><SeverityBadge severity={row.severity} /></TableCell>
                      <TableCell>{row.occurrence_count}</TableCell>
                      <TableCell>{formatDate(row.first_seen)}</TableCell>
                      <TableCell>{formatDate(row.last_seen)}</TableCell>
                      <TableCell><StatusPill status={row.active ? 'active' : 'resolved'} /></TableCell>
                    </TableRow>
                  )}
                />
              )
            )
          }
        </div>
      </Collapse>
    </div>
  );
};

export default DataIntegrityWarningsSection;
